<template>
  <div class="scenario-container muzik-composer mk-container">
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <div v-if="loaded && errorStatus">
      <NotFoundDummy
        :class-name="'abreast'"
        :title="$t('DUMMY.NOT_FOUND_TITLE')"
        :desp="$t('DUMMY.NOT_FOUND_DESP')"
      />
    </div>
    <div v-else-if="loaded">
      <LayoutInnerPage v-if="composerData">
        <template slot="left">
          <PeopleInfo
            :img-src="composerData.file_link"
            :title="composerName"
            :sub-title="displayComposerName(composerData, 'en-us')"
            :alias-title="$t('COMPOSER.COMPOSER_ALIAS')"
            :alias="displayComposerAliases(composerData)"
            type="composer"
          />
          <PeopleGenreTags :content="workData" :locale="locale" :category="genreData" />
          <Paganiniplus />
        </template>
        <template slot="right">
          <PeopleDesp :content="displayComposerDescription(composerData)" />
          <ArticleSuggested :keyword="composerLastName" />
          <PeopleAccordion :content="workData" :locale="locale" :category="genreData" type="work" />
        </template>
      </LayoutInnerPage>
      <CommonDummy v-else :title="$t('DUMMY.LISTEN_COMMON')" />
    </div>
    <div v-else class="loader-container">
      <Loader />
    </div>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/common/Breadcrumbs'
import CommonDummy from '@/components/common/DummyStatus/CommonDummy'
import NotFoundDummy from '@/components/common/DummyStatus/NotFoundDummy'
import Loader from '@/components/common/Loader'
import Paganiniplus from '@/components/tsp/Paganiniplus'
import LayoutInnerPage from '@/components/people/LayoutInnerPage'
import PeopleInfo from '@/components/people/PeopleInfo'
import PeopleGenreTags from '@/components/people/PeopleGenreTags'
import PeopleDesp from '@/components/people/PeopleDesp'
import PeopleAccordion from '@/components/people/PeopleAccordion'
import ArticleSuggested from '@/components/article/ArticleSuggested'
import PeopleService from '@/assets/services/PeopleService'
import WorkService from '@/assets/services/WorkService'
import GenreService from '@/assets/services/GenreService'
import { displayComposerName, displayComposerAliases, displayComposerDescription } from '@/assets/helpers/TrackHelper'
import { displayWorkName } from '@/assets/helpers/WorkHelper'

export default {
  name: 'ComposerById',
  components: {
    Paganiniplus,
    NotFoundDummy,
    Loader,
    Breadcrumbs,
    CommonDummy,
    LayoutInnerPage,
    PeopleInfo,
    PeopleGenreTags,
    PeopleDesp,
    PeopleAccordion,
    ArticleSuggested
  },
  data() {
    return {
      loaded: false,
      errorStatus: false,
      composerData: {},
      workData: {},
      genreData: {}
    }
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        ...this.$route.meta.metaInfo.meta,
        { vmid: 'name', name: 'name', content: this.title },
        { vmid: 'description', name: 'description', content: this.description },
        { vmid: 'keywords', name: 'keywords', content: this.keywords },
        { vmid: 'image', name: 'image', content: this.cover },
        { vmid: 'og:title', name: 'og:title', content: this.title },
        { vmid: 'og:description', name: 'og:description', content: this.description },
        { vmid: 'og:image', name: 'og:image', content: this.cover }
      ],
      link: [
        { vmid: 'canonical', rel: 'canonical', href: this.canonicalURL },
        { vmid: 'image_src', rel: 'image_src', href: this.cover }
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [{ innerHTML: JSON.stringify(this.structuredData), type: 'application/ld+json' }]
    }
  },
  computed: {
    breadcrumbs() {
      let breadcrumbsAry = []
      const indexBreadcrumbs = { routeName: 'index', localeLabel: 'LISTEN.TITLE', redirectPath: '' }
      const composerListBreadcrumbs = { routeName: '', localeLabel: 'COMPOSER.INDEX.TITLE', redirectPath: '' }
      breadcrumbsAry.push(indexBreadcrumbs)
      breadcrumbsAry.push(composerListBreadcrumbs)
      if (this.composerName) {
        const composerBreadcrumbs = {
          routeName: 'composerById',
          routeParams: {
            id: this.composerData.id
          },
          localeLabel: this.composerName || 'COMMON.COMPOSER',
          redirectPath: ''
        }
        breadcrumbsAry.push(composerBreadcrumbs)
      }
      return breadcrumbsAry
    },
    locale() {
      return this.$i18n.locale.toLowerCase()
    },
    composerName() {
      return this.displayComposerName(this.composerData)
    },
    composerLastName() {
      return (this.composerName && this.composerName.split(',')[0]) || this.composerName
    },
    // default meta data, update after fetched composer
    title() {
      const postfixTitle = `${this.$t('LISTEN.TITLE')} | MUZIK AIR`
      return this.composerData && this.composerName
        ? `${this.composerName} | ${postfixTitle}`
        : `${this.$t('COMMON.COMPOSER')} | ${postfixTitle}`
    },
    description() {
      return this.composerData
        ? `${this.displayComposerDescription(this.composerData).slice(0, 200)}` || this.$t('COMMON.COMPOSER')
        : this.$t('COMMON.COMPOSER')
    },
    keywords() {
      let keywordsAry = []
      keywordsAry.push(this.$t('COMMON.COMPOSER'))
      keywordsAry.push(this.composerName)
      // keywordsAry.push(this.description)
      if (this.workData) {
        Object.keys(this.workData).forEach(movement => {
          Object.keys(this.workData[movement]).forEach(item => {
            keywordsAry.push(this.displayWorkName(this.workData[movement][item]))
          })
        })
      }
      return keywordsAry.join(', ')
    },
    cover() {
      return this.composerData && this.composerData.file_link
        ? this.composerData.file_link
        : `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/img/cover-social/listen.png`
    },
    canonicalURL() {
      return this.composerData && this.composerData.id
        ? `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/composers/${this.composerData.id}`
        : `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`
    },
    structuredData() {
      const structuredData = [
        {
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@id': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/`,
                name: this.$t('LISTEN.TITLE')
              }
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@id': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/composers`,
                name: this.$t('COMPOSER.INDEX.TITLE')
              }
            }
          ]
        }
      ]
      const workStructuredData = {
        '@context': 'http://schema.org',
        '@type': 'MusicGroup',
        image: this.cover,
        name: this.composerName,
        url: this.canonicalURL,
        description: this.description,
        album: []
      }
      if (this.work) {
        this.work.forEach(work => {
          workStructuredData.album.push({
            '@type': 'MusicAlbum',
            '@id': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/composers/works/${work.id}`,
            name: this.displayWorkName(work)
          })
        })
      }
      structuredData.push(workStructuredData)
      return structuredData
    }
  },
  watch: {
    '$route.params.id'(val) {
      this.getComposer()
    }
  },
  mounted() {
    this.getComposer()
  },
  methods: {
    displayComposerName,
    displayComposerAliases,
    displayComposerDescription,
    displayWorkName,
    parseRewriteURL(unparsedIdStr) {
      const MuzikListenV4Reg = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}/gi
      const MuzikListenV3Reg = /^[A-Z\d]{6}/gi
      let parsedIdStr = ''
      if (MuzikListenV4Reg.test(unparsedIdStr)) {
        parsedIdStr = unparsedIdStr.match(MuzikListenV4Reg)[0] // MUZIK LISTEN v4 (UUID.v4) id format
      } else if (MuzikListenV3Reg.test(unparsedIdStr) && (unparsedIdStr.length === 6)) {
        parsedIdStr = unparsedIdStr.match(MuzikListenV3Reg)[0] // MUZIK LISTEN v3 id format
      } else {
        parsedIdStr = unparsedIdStr
      }
      return parsedIdStr
    },
    async getComposer() {
      this.loaded = false
      const unparsedIdStr = this.$route.params.id.replace('.html', '') // external share link
      const composerId = this.parseRewriteURL(unparsedIdStr)
      const composerRewrite = unparsedIdStr.replace(composerId, '')
      // const composerId = this.$route.params.id
      let res = await PeopleService.getPeople(composerId)
      if (res !== null) {
        let res2 = await WorkService.getWorkList({ people_id: composerId, list: 'composer' })
        if (res2) {
          res2.data.forEach(item => {
            if (item.genre_id !== null) {
              if (!this.workData[item.genre_id]) {
                this.workData[item.genre_id] = {}
              }
              this.workData[item.genre_id][item.id] = {}
              this.workData[item.genre_id][item.id].langs = item.langs
            }
          })
          let res3 = await GenreService.getGenreList('', '', 1, 250)
          let tmpGenre = {}
          res3.data.forEach(item => {
            tmpGenre[item.id] = {}
            item.langs.forEach(lang => {
              tmpGenre[item.id][lang.locale] = lang
            })
          })
          this.composerData = res
          this.genreData = tmpGenre
          this.loaded = true
          if (this.composerData.id !== unparsedIdStr) {
            // rewrite old version id
            this.$router.replace({ params: { id: this.composerData.id } })
          }
        } else {
          this.composerData = {}
          this.loaded = true
          this.errorStatus = true
        }
      } else {
        if (composerRewrite) {
          this.loaded = true
          const queryKeyword = composerRewrite.split('-').join(' ')
          this.$router.push({
            name: 'search',
            query: {
              type: 'composer',
              q: queryKeyword
            }
          })
        } else {
          this.loaded = true
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';

.muzik-composer {
  // padding: 0 $padding-lg-grid-x1 66px;
  padding-bottom: 99px;
  .breadcrumbs {
    padding: 24px 0;
  }
  .dummy {
    text-align: left;
    margin-bottom: 60px;
  }
  .loader-container {
    text-align: center;
    .loader {
      margin-bottom: 40px;
    }
  }
}

@media only screen and (min-width: 1440px) {
  // default
}

@media only screen and (max-width: 1439.98px) {
  .muzik-composer {
    padding-bottom: 48px;
    // padding: 0 $padding-md-grid-x1;
  }
}

@media only screen and (max-width: 1023.98px) {
  .muzik-composer {
    padding-bottom: 48px;
    // padding: 0 $padding-sm-grid-x1;
    .dummy {
      display: none;
    }
  }
}

@media only screen and (max-width: 767.98px) {
  .muzik-composer {
    padding-bottom: 48px;
  }
  // null
}
</style>
